/**
 * Revolve kernel — lathe profile regions around an axis in the sketch plane.
 *
 * Geometry: express each profile point as (h, r) — along-axis and radial
 * coordinates. A point at angle t maps to A0 + h·Â + r·Dir(t), where
 * Dir(t) = cosT·ê1 + sinT·n̂ (ê1 = in-plane perpendicular to the axis,
 * n̂ = sketch plane normal). Walls are flat-shaded quads; partial revolves
 * get earcut end caps. Profiles must stay on one side of the axis.
 */

import earcut from 'earcut';

import { ensureOutward } from './meshUtils';
import { uvToWorld, type Loop } from './sketch';
import type { MeshData, ProfileRegion } from './types';
import type { Vec2 } from '../timeline/types';

const FULL = Math.PI * 2;
const EPS = 1e-6;

export function revolveProfiles(
  regions: readonly ProfileRegion[],
  axisOrigin: Vec2,
  axisDirection: Vec2,
  angle: number,
): MeshData {
  const positions: number[] = [];
  const normals: number[] = [];
  const indices: number[] = [];
  const edges: number[] = [];
  for (const region of regions) {
    revolveRegion(region, axisOrigin, axisDirection, angle, positions, normals, indices, edges);
  }
  const mesh: MeshData = {
    positions: new Float32Array(positions),
    normals: new Float32Array(normals),
    indices: new Uint32Array(indices),
    edgePositions: new Float32Array(edges),
  };
  return ensureOutward(mesh);
}

function revolveRegion(
  region: ProfileRegion,
  axisOrigin: Vec2,
  axisDirection: Vec2,
  angle: number,
  positions: number[],
  normals: number[],
  indices: number[],
  edges: number[],
): void {
  const m = region.planeToWorld;
  const alen = Math.hypot(axisDirection[0], axisDirection[1]);
  if (alen < EPS) throw new Error('Revolve axis direction is zero');
  const ax = axisDirection[0] / alen;
  const ay = axisDirection[1] / alen;

  const span = Math.min(Math.max(Math.abs(angle), 1e-3), FULL);
  const closed = span > FULL - 1e-6;
  const steps = Math.max(8, Math.ceil((48 * span) / FULL));

  // World-space frame: origin, axis Â, radial ê1 (in-plane ⊥axis), n̂.
  const o = uvToWorld(m, axisOrigin);
  const uAxis = uvToWorld(m, [axisOrigin[0] + ax, axisOrigin[1] + ay]);
  const A: V3 = [uAxis[0] - o[0], uAxis[1] - o[1], uAxis[2] - o[2]];
  const uPerp = uvToWorld(m, [axisOrigin[0] - ay, axisOrigin[1] + ax]);
  const E1: V3 = [uPerp[0] - o[0], uPerp[1] - o[1], uPerp[2] - o[2]];
  const N: V3 = [m[8]!, m[9]!, m[10]!];

  // Profile point → (h, r); reject axis-crossing profiles.
  const toHR = (p: Vec2): [number, number] => {
    const dx = p[0] - axisOrigin[0];
    const dy = p[1] - axisOrigin[1];
    return [dx * ax + dy * ay, -dx * ay + dy * ax];
  };
  const loops = [region.outer, ...region.holes];
  let side = 0;
  for (const loop of loops) {
    for (const p of loop) {
      const r = toHR(p)[1];
      if (Math.abs(r) > EPS) {
        if (side === 0) side = Math.sign(r);
        else if (Math.sign(r) !== side) {
          throw new Error('Revolve profile crosses the axis — keep it on one side');
        }
      }
    }
  }

  const point = (h: number, r: number, t: number): V3 => {
    const ct = Math.cos(t);
    const st = Math.sin(t);
    return [
      o[0] + h * A[0] + r * (ct * E1[0] + st * N[0]),
      o[1] + h * A[1] + r * (ct * E1[1] + st * N[1]),
      o[2] + h * A[2] + r * (ct * E1[2] + st * N[2]),
    ];
  };

  const quad = (a: V3, b: V3, c: V3, d: V3): void => {
    // Two triangles (a,b,c) (a,c,d) with the face normal; skip degenerates.
    const n = triNormal(a, b, c) ?? triNormal(a, c, d);
    if (!n) return;
    const base = positions.length / 3;
    for (const v of [a, b, c, d]) {
      positions.push(...v);
      normals.push(...n);
    }
    if (triNormal(a, b, c)) indices.push(base, base + 1, base + 2);
    if (triNormal(a, c, d)) indices.push(base, base + 2, base + 3);
  };

  // --- walls -----------------------------------------------------------------
  for (const loop of loops) {
    const hr = loop.map(toHR);
    for (let k = 0; k < hr.length; k++) {
      const [h0, r0] = hr[k]!;
      const [h1, r1] = hr[(k + 1) % hr.length]!;
      for (let j = 0; j < steps; j++) {
        const t0 = (span * j) / steps;
        const t1 = (span * (j + 1)) / steps;
        quad(point(h0, r0, t0), point(h1, r1, t0), point(h1, r1, t1), point(h0, r0, t1));
      }
      // Display edges: circles traced by sharp profile corners.
      const [h2, r2] = hr[(k + 2) % hr.length]!;
      if (cornerIsSharp([h0, r0], [h1, r1], [h2, r2])) {
        for (let j = 0; j < steps; j++) {
          edges.push(
            ...point(h1, r1, (span * j) / steps),
            ...point(h1, r1, (span * (j + 1)) / steps),
          );
        }
      }
    }
  }

  // --- end caps (partial revolves) --------------------------------------------
  if (!closed) {
    const flat: number[] = [];
    const holeStarts: number[] = [];
    const outerHR = region.outer.map(toHR);
    for (const [h, r] of outerHR) flat.push(h, r);
    for (const hole of region.holes) {
      holeStarts.push(flat.length / 2);
      for (const [h, r] of hole.map(toHR)) flat.push(h, r);
    }
    const tris = earcut(flat, holeStarts.length ? holeStarts : undefined);
    const capVerts: [number, number][] = [];
    for (let i = 0; i < flat.length; i += 2) capVerts.push([flat[i]!, flat[i + 1]!]);

    for (const [t, reverse] of [
      [0, true], // start cap faces backward along the sweep
      [span, false],
    ] as const) {
      const base = positions.length / 3;
      // Cap normal = sweep tangent direction (±).
      const st = Math.sin(t);
      const ct = Math.cos(t);
      const sign = reverse ? -1 : 1;
      const n: V3 = [
        sign * (-st * E1[0] + ct * N[0]),
        sign * (-st * E1[1] + ct * N[1]),
        sign * (-st * E1[2] + ct * N[2]),
      ];
      for (const [h, r] of capVerts) {
        positions.push(...point(h, r, t));
        normals.push(...n);
      }
      for (let i = 0; i < tris.length; i += 3) {
        if (reverse) indices.push(base + tris[i]!, base + tris[i + 2]!, base + tris[i + 1]!);
        else indices.push(base + tris[i]!, base + tris[i + 1]!, base + tris[i + 2]!);
      }
      // Cap outline edges.
      for (const loop of loops) {
        const hr = loop.map(toHR);
        for (let k = 0; k < hr.length; k++) {
          const [h0, r0] = hr[k]!;
          const [h1, r1] = hr[(k + 1) % hr.length]!;
          edges.push(...point(h0, r0, t), ...point(h1, r1, t));
        }
      }
    }
  }
}

type V3 = [number, number, number];

function triNormal(a: V3, b: V3, c: V3): V3 | null {
  const ux = b[0] - a[0], uy = b[1] - a[1], uz = b[2] - a[2];
  const vx = c[0] - a[0], vy = c[1] - a[1], vz = c[2] - a[2];
  const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
  const len = Math.hypot(nx, ny, nz);
  if (len < 1e-10) return null;
  return [nx / len, ny / len, nz / len];
}

function cornerIsSharp(p0: readonly number[], p1: readonly number[], p2: readonly number[]): boolean {
  const d0x = p1[0]! - p0[0]!, d0y = p1[1]! - p0[1]!;
  const d1x = p2[0]! - p1[0]!, d1y = p2[1]! - p1[1]!;
  const l0 = Math.hypot(d0x, d0y) || 1;
  const l1 = Math.hypot(d1x, d1y) || 1;
  return (d0x / l0) * (d1x / l1) + (d0y / l0) * (d1y / l1) < Math.cos(Math.PI / 6);
}

export type { Loop };
